// Pure builders for the KPI caption lines (previous-period context and
// no-data messages). Kept free of React so the logic is unit-testable.
import { formatCurrency } from '../../lib/format';
import type { DashboardDateBucket, DashboardPeriodBucket, DashboardResponse } from '@aid/shared';

export type DashboardT = (key: string, options?: Record<string, unknown>) => string;

export interface KpiCaptions {
  cobrosMesSinDatos: boolean;
  pagosMesSinDatos: boolean;
  cobrosHoy: string | undefined;
  cobrosMes: string | undefined;
  pagosHoy: string | undefined;
  pagosMes: string | undefined;
}

function prevDayContext(t: DashboardT, bucket: DashboardDateBucket): string | undefined {
  return bucket.conteo > 0
    ? t('dashboard.ayer', { monto: formatCurrency(bucket.total) })
    : undefined;
}

function prevMonthContext(
  t: DashboardT,
  bucket: DashboardPeriodBucket,
  prevMonthName: string,
): string | undefined {
  return bucket.conteo > 0
    ? t('dashboard.mesAnteriorCorte', { mes: prevMonthName, monto: formatCurrency(bucket.total) })
    : undefined;
}

export interface MovimientoCard {
  title: string;
  description: string;
}

/** Builds the "Movimiento de Clientes" card title and description. */
export function buildMovimientoCard(t: DashboardT): MovimientoCard {
  return {
    title: t('dashboard.movimientoClientesTitle'),
    description: t('dashboard.movimientoClientesDescripcion'),
  };
}

/** Builds the four KPI captions plus the "no data this month" flags. */
export function buildKpiCaptions(
  t: DashboardT,
  data: DashboardResponse,
  monthName: string,
  prevMonthName: string,
): KpiCaptions {
  const { cobros, pagos } = data;
  const cobrosMesSinDatos = cobros.currentMonth.conteo === 0;
  const pagosMesSinDatos = pagos.currentMonth.conteo === 0;

  return {
    cobrosMesSinDatos,
    pagosMesSinDatos,
    cobrosHoy:
      cobros.currentDay.conteo === 0
        ? t('dashboard.sinCobrosHoy')
        : prevDayContext(t, cobros.previousDay),
    cobrosMes: cobrosMesSinDatos
      ? t('dashboard.sinCobrosMes', { mes: monthName })
      : prevMonthContext(t, cobros.previousMonth, prevMonthName),
    pagosHoy:
      pagos.currentDay.conteo === 0
        ? t('dashboard.sinPagosHoy')
        : prevDayContext(t, pagos.previousDay),
    pagosMes: pagosMesSinDatos
      ? t('dashboard.sinPagosMes', { mes: monthName })
      : prevMonthContext(t, pagos.previousMonth, prevMonthName),
  };
}
